import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import {
  cast,
  firstRecord,
  throwOnFirstError,
  toPostgrestIlikeContains,
} from "@/lib/utils/query-helpers";

/**
 * Shared access port for the three finance document kinds.
 *
 * The invoice document surface was copied to estimates and purchase orders and
 * the copies drifted — one of them all the way to an unclamped page offset.
 * This module is where the shared semantics live exactly once, keyed by
 * document kind and assembled from the primitives that already exist
 * (`firstRecord`, `throwOnFirstError`, `toPostgrestIlikeContains`,
 * `list-order.ts`, `list-totals.ts`) rather than written fresh. It is
 * deliberately NOT a generic document client: the `p_*` RPC mappings whose
 * shapes differ per kind stay in the feature clients, and per-kind child
 * tables are declared by descriptors the clients own.
 *
 * Authorization stays in RLS. Nothing here filters, hides, or admits rows on
 * the client: every predicate this module applies is a caller-declared
 * server-side scope, and rows come back exactly as the backend sent them.
 */

/** The three document kinds the shared access port serves. */
export type FinanceDocumentKind = "invoice" | "estimate" | "purchase_order";

interface FinanceDocumentArchiveContract {
  /** Error raised when the archive RPC returns no row. Names the operation. */
  failureMessage: string;
  /** RPC parameter naming the document row to archive. */
  idParam: string;
  rpc: string;
}

interface FinanceDocumentDescriptor {
  archive: FinanceDocumentArchiveContract;
  /** Header columns every shared-select read of this kind returns. */
  headerSelect: string;
  table: "gl_estimates" | "gl_invoices" | "gl_purchase_orders";
}

/**
 * Header columns all three kinds share. Each kind appends its own columns and
 * its account join below, so the shared half cannot drift per kind.
 */
const FINANCE_DOCUMENT_HEADER_CORE = `id, uid, date, notes, total_amount, total_paid, balance, payment_status,
      account_id, created_at, pdf_url`;

/**
 * Line-item columns the invoice and estimate line selects share. Each client
 * appends its own `gl_products` join — the joined columns differ per kind.
 */
export const FINANCE_DOCUMENT_LINE_CORE = `id, product_name, description, quantity, selling_price, unit_cost, line_total, product_id, date_of_sale`;

/**
 * Everything the port knows per kind, in one table. Lookup only — the port's
 * own code never branches on the kind.
 */
const FINANCE_DOCUMENTS: Record<FinanceDocumentKind, FinanceDocumentDescriptor> = {
  estimate: {
    archive: {
      failureMessage: "Failed to archive estimate.",
      idParam: "p_estimate_id",
      rpc: "xan_soft_delete_estimate",
    },
    headerSelect: `
      ${FINANCE_DOCUMENT_HEADER_CORE},
      is_converted, is_sample, is_quick, detail_completed_at, project_id,
      converted_date, converted_invoice_id,
      gl_accounts!gl_estimates_account_id_fkey ( id, name, uid )
    `,
    table: "gl_estimates",
  },
  invoice: {
    archive: {
      failureMessage: "Failed to archive invoice.",
      idParam: "p_invoice_id",
      rpc: "xan_soft_delete_invoice",
    },
    headerSelect: `
      ${FINANCE_DOCUMENT_HEADER_CORE},
      gl_accounts!gl_invoices_account_id_fkey ( id, name, uid )
    `,
    table: "gl_invoices",
  },
  purchase_order: {
    archive: {
      failureMessage: "Failed to archive purchase order.",
      idParam: "p_po_id",
      rpc: "xan_soft_delete_purchase_order",
    },
    headerSelect: `
      ${FINANCE_DOCUMENT_HEADER_CORE},
      is_paid, product_count, total_quantity,
      gl_accounts!gl_purchase_orders_account_id_fkey ( id, name, uid )
    `,
    table: "gl_purchase_orders",
  },
};

/** The shared header select for one kind, for reads that re-select after a write. */
export function financeDocumentHeaderSelect(kind: FinanceDocumentKind): string {
  return FINANCE_DOCUMENTS[kind].headerSelect;
}

/**
 * Starts a header read for one document kind: the kind's table with the shared
 * header select and nothing else. No visibility predicate is applied here or
 * anywhere in this module — which rows exist is RLS's decision alone.
 *
 * `TRow` is the caller's declared row shape; the select narrows and joins
 * columns, so the generated table row type would be a lie either way. The cast
 * is confined to this one function.
 */
export function financeDocumentQuery<TRow = Record<string, unknown>>(
  kind: FinanceDocumentKind,
): QueryBuilder<TRow> {
  const descriptor = FINANCE_DOCUMENTS[kind];
  return getBackendClient()
    .data.from(descriptor.table)
    .select(descriptor.headerSelect) as unknown as QueryBuilder<TRow>;
}

/** The resolved window of one paged list read. */
export interface FinanceListPage {
  limit: number;
  offset: number;
}

/**
 * Resolves the page window for a paged list read, identically for every kind:
 * a missing or negative offset clamps to `0`, so an out-of-range request
 * returns the first page instead of handing PostgREST a malformed range.
 */
export function resolveFinanceListPage(
  params: { limit?: number; offset?: number } | undefined,
  defaultLimit: number,
): FinanceListPage {
  return {
    limit: params?.limit ?? defaultLimit,
    offset: Math.max(0, params?.offset ?? 0),
  };
}

/**
 * Applies a resolved page window as the read's `.range()`, for the reads whose
 * leading sort keys are decided elsewhere (the purchase-order merge-target
 * lookup). The caller owns closing the order with `tiebreakListOrder` — every
 * other list read goes through {@link applyFinanceListSortedPage}, where the
 * window and the total order are inseparable.
 */
export function applyFinanceListPage<T>(
  query: QueryBuilder<T>,
  page: FinanceListPage,
): QueryBuilder<T> {
  return query.range(page.offset, page.offset + page.limit - 1);
}

/**
 * Orders a paged read and applies its clamped window in one operation: the
 * caller's leading sort, the unique tiebreak `orderListPage` appends, then the
 * `.range()`. Paging through here cannot be separated from a total order —
 * the invariant every `.range()` window needs to be a genuine slice.
 */
export function applyFinanceListSortedPage<T>(
  query: QueryBuilder<T>,
  sort: { ascending: boolean; column: string },
  page: FinanceListPage,
): QueryBuilder<T> {
  return applyFinanceListPage(orderListPage(query, sort), page);
}

/**
 * Free-text search over the `search_text` computed column — one predicate,
 * applied the same way for every kind, and the same SQL the `*_list_totals`
 * RPCs filter with. `%` and `_` in the term match literally via
 * `toPostgrestIlikeContains`; a blank term applies no filter at all.
 */
export function applyFinanceDocumentSearch<T>(
  query: QueryBuilder<T>,
  search: string | undefined,
): QueryBuilder<T> {
  const term = search?.trim();
  if (!term) {
    return query;
  }
  return query.or(`search_text.ilike.${toPostgrestIlikeContains(term)}`);
}

/**
 * One child relation of a document detail read. Child tables differ per kind,
 * so each feature client declares its own descriptors and the port resolves
 * them without knowing which kind it is serving.
 */
export interface FinanceDocumentChildDescriptor {
  /**
   * Extra server-side equality scopes (e.g. `is_deleted = false`), applied
   * after the parent key. Scopes narrow the query the backend runs — they are
   * never applied to rows after they arrive.
   */
  readonly alsoEq?: readonly (readonly [column: string, value: unknown])[];
  /** Starts the child read: its table and select, before the port scopes it. */
  readonly from: () => QueryBuilder<unknown>;
  readonly order: { readonly ascending: boolean; readonly column: string };
  /** Child column that references the parent document row. */
  readonly parentColumn: string;
}

/**
 * Resolves a detail read's child queries in parallel, each scoped to the
 * parent document. The first error surfaces instead of a partially assembled
 * detail, and rows come back exactly as the backend sent them, in descriptor
 * order — no client-side filtering, reshaping, or write-origin rewriting.
 */
export async function readFinanceDocumentChildren(
  documentId: string,
  children: readonly FinanceDocumentChildDescriptor[],
): Promise<unknown[][]> {
  const results = await Promise.all(
    children.map((child) => {
      let query = child.from().eq(child.parentColumn, documentId);
      for (const [column, value] of child.alsoEq ?? []) {
        query = query.eq(column, value);
      }
      return query.order(child.order.column, { ascending: child.order.ascending });
    }),
  );

  throwOnFirstError(results);

  return results.map((result) => cast<unknown[]>(result.data ?? []));
}

/**
 * Archives one document through its kind's soft-delete RPC and unwraps the
 * single-row result identically for every kind: the RPC may return the row
 * bare or wrapped in a one-element array, and a missing row is a named
 * failure — never a silent success.
 */
export async function archiveFinanceDocument(
  kind: FinanceDocumentKind,
  documentId: string,
): Promise<{ archive_id: string; success: boolean }> {
  const { archive } = FINANCE_DOCUMENTS[kind];
  const { data, error } = await getBackendClient().data.rpc(archive.rpc, {
    [archive.idParam]: documentId,
  });

  if (error) {
    throw new Error(error.message);
  }

  const row = firstRecord(data);
  if (!row) {
    throw new Error(archive.failureMessage);
  }

  return { archive_id: String(row.archive_id), success: true };
}

/**
 * The marker every application-originated finance-document write carries in
 * `last_modified_by_source`. Glide-sync reads this column to decide sync
 * direction, so the value is stamped through the one builder below instead of
 * per call site (the precedent is `buildProductUpdates` in `products.ts`).
 * Rows that arrive carrying Glide's own marker pass through reads untouched —
 * nothing in this module rewrites the column.
 */
const APP_WRITE_ORIGIN = "database";

/**
 * Stamps an app-originated write with the shared write-origin marker. The
 * stamp is applied last, so a colliding key in `values` can never smuggle a
 * different origin past it.
 */
export function withAppWriteOrigin(values: Record<string, unknown>): Record<string, unknown> {
  return { ...values, last_modified_by_source: APP_WRITE_ORIGIN };
}
